/**
 * CutestPluckRulesPage — long-form "Contest rules" page for the
 * Cutest Pluck on the Block contest (Rules Page Item A, 2026-07-23).
 *
 * All prize, date, and winner-count values render DYNAMICALLY from
 * `useActiveContest()` — a PATCH to `/admin/contest/config` on prod
 * (bumping `prize_amount_cents`, adjusting `entry_close_at`, etc.)
 * propagates to this page WITHOUT a frontend redeploy. The static-
 * source drift-guard `crp-drift-01` locks the page against future
 * refactors that might reintroduce a hardcoded dollar / date value.
 *
 * Content contract (human-authorized items 1-14 in the Item A dispatch):
 *   1.  Sponsor: "PooPluckers" (no LLC)
 *   2.  Eligibility: 18+, US residents
 *   3.  Entry limit: one per ACCOUNT (not per household)
 *   4.  Entry + voting periods: dynamic dates (America/New_York
 *       display, "TBD" fallback)
 *   5.  Winner determination: highest vote count; ties → earliest
 *       submission (NOT publishing the tertiary microsecond edge)
 *   6.  Number of winners: dynamic winner_count
 *   7.  Prize: dynamic dollar amount + platform-credit language
 *       matching the winner email
 *   8.  Photo use: quotes CONTEST_CONSENT_TEXT verbatim
 *   9.  Voting conduct: 1 vote / person / contest; movable;
 *       manual-review disqualification (NO automated detection)
 *   10. Winner notification: by email (fork-A per human's Q2 GO)
 *       + spam-folder hedge
 *   11. Rejection notification: passive pull ("check your entry
 *       page for status") — no email
 *   12. Errors + fraud clause
 *   13. Facebook / Instagram platform disclaimer
 *   14. Rules subject to Terms (footer reference)
 *
 * Route: /cutest-pluck/rules. Registered in App.js alongside
 * /cutest-pluck, /cutest-pluck/enter, and /cutest-pluck/entries/:id.
 *
 * Family-L discipline: no inline hex; Tailwind tokens only. No
 * emoji icons (fontawesome / lucide-react are the design-guide
 * choice).
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicPageShell from "../../components/PublicPageShell";
import { useActiveContest } from "@/context/ActiveContestContext";
import {
  CONTEST_CONSENT_TEXT,
  getContestDisplayName,
} from "../../lib/contestConstants";

// Fallback default matches backend seed (`contest_rounds.py:124`)
// so the brief loading window shows a sensible value before
// /public/contest/active resolves.
const DEFAULT_PRIZE_AMOUNT_CENTS = 2100;
const DEFAULT_WINNER_COUNT = 10;

/**
 * Format an ISO-8601 UTC timestamp as a human-readable date/time in
 * America/New_York with "TBD" fallback for null/undefined. Chosen
 * timezone matches the primary audience; ET label is displayed so
 * users in other zones can convert.
 */
function formatEasternDateTime(iso) {
  if (!iso || typeof iso !== "string") return "TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBD";
  // Intl.DateTimeFormat handles DST for America/New_York.
  const opts = {
    timeZone: "America/New_York",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const formatted = new Intl.DateTimeFormat("en-US", opts).format(d);
  // "August 9, 2026 at 11:59 PM" → append "ET" label.
  return `${formatted.replace(", ", ", ").replace(" at ", " at ")} ET`;
}

/** Whole-dollar or fractional — mirrors `formatPrizeDollars` in
 *  CutestPluckLandingPage. Kept as a local helper (drift-guard would
 *  fire on any hardcoded "$21" in the rules-page source).  */
function formatPrizeDollars(cents) {
  const c = Number.isFinite(cents) ? cents : DEFAULT_PRIZE_AMOUNT_CENTS;
  const dollars = c / 100;
  return dollars % 1 === 0
    ? `$${dollars.toFixed(0)}`
    : `$${dollars.toFixed(2)}`;
}

export default function CutestPluckRulesPage() {
  const activeContest = useActiveContest();
  const {
    prizeAmountCents,
    entryOpenAt,
    entryCloseAt,
    votingCloseAt,
    winnerCount,
  } = activeContest;

  // Format-once memoization — every dynamic value routes through
  // context so the drift-guard test can confirm the compiled JSX
  // holds no hardcoded dollar/date/count. `contestName` flows
  // through `getContestDisplayName` (Item 3 seam, 2026-07-24) so
  // an internal round ID like "cutest_pluck_2026 Round 1 (legacy)"
  // never leaks to the public `<title>`, subhead, or meta
  // description. Guarded by `contestNameLeak.driftGuard.test.jsx`.
  const display = useMemo(
    () => ({
      contestName: getContestDisplayName(activeContest),
      prize: formatPrizeDollars(prizeAmountCents),
      entryOpen: formatEasternDateTime(entryOpenAt),
      entryClose: formatEasternDateTime(entryCloseAt),
      votingClose: formatEasternDateTime(votingCloseAt),
      winnerCount: (
        Number.isFinite(winnerCount) && winnerCount > 0
          ? winnerCount
          : DEFAULT_WINNER_COUNT
      ),
    }),
    [activeContest, prizeAmountCents, entryOpenAt, entryCloseAt, votingCloseAt, winnerCount],
  );

  return (
    <PublicPageShell mainClass="px-6 py-12">
      <Helmet>
        <title>{`Contest Rules — ${display.contestName} — PooPluckers`}</title>
        <meta
          name="description"
          content={`Official rules for the ${display.contestName} contest on PooPluckers.com.`}
        />
        <link rel="canonical" href={
          typeof window !== "undefined"
            ? `${window.location.origin}/cutest-pluck/rules`
            : "/cutest-pluck/rules"
        } />
      </Helmet>
      <article
        className="mx-auto max-w-2xl space-y-6 text-sm leading-relaxed text-pp-text"
        data-testid="cutest-pluck-rules-page"
      >
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-pp-text-muted">
            <Link
              to="/cutest-pluck"
              className="underline decoration-pp-orange decoration-2 underline-offset-4 hover:text-pp-orange"
              data-testid="cutest-pluck-rules-back-link"
            >
              &larr; Back to the contest
            </Link>
          </p>
          <h1 className="text-3xl font-bold text-pp-text-primary">
            Contest rules
          </h1>
          <p
            className="text-pp-text-muted"
            data-testid="cutest-pluck-rules-subhead"
          >
            {display.contestName} — official rules
          </p>
        </header>

        {/* 1. Sponsor */}
        <section data-testid="cutest-pluck-rules-sponsor">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            1. Sponsor
          </h2>
          <p>
            This contest is sponsored by <strong>PooPluckers</strong>.
            Contact us via <a
              href="https://poopluckers.com"
              className="underline hover:text-pp-orange"
            >PooPluckers.com</a>.
          </p>
        </section>

        {/* 2. Eligibility */}
        <section data-testid="cutest-pluck-rules-eligibility">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            2. Eligibility
          </h2>
          <p>
            Open to legal residents of the United States who are at
            least 18 years old at the time of entry.
          </p>
        </section>

        {/* 3. Entry limit — one per ACCOUNT (NOT per household) */}
        <section data-testid="cutest-pluck-rules-entry-limit">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            3. Entry limit
          </h2>
          <p>
            <strong>One entry per account.</strong> Only one entry
            may be submitted from any single PooPluckers account.
          </p>
        </section>

        {/* 4. Entry + voting periods — dynamic */}
        <section data-testid="cutest-pluck-rules-periods">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            4. Entry and voting periods
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>Entries open:</strong>{" "}
              <span data-testid="cutest-pluck-rules-entry-open">
                {display.entryOpen}
              </span>
            </li>
            <li>
              <strong>Entries close:</strong>{" "}
              <span data-testid="cutest-pluck-rules-entry-close">
                {display.entryClose}
              </span>
            </li>
            <li>
              <strong>Voting closes:</strong>{" "}
              <span data-testid="cutest-pluck-rules-voting-close">
                {display.votingClose}
              </span>
            </li>
          </ul>
        </section>

        {/* 5. Winner determination — DO NOT publish microsecond edge */}
        <section data-testid="cutest-pluck-rules-winner-determination">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            5. How winners are determined
          </h2>
          <p>
            <strong>Winners are selected by highest vote count.
            In the event of a tie, the entry submitted earliest
            wins.</strong>
          </p>
        </section>

        {/* 6. Number of winners — dynamic */}
        <section data-testid="cutest-pluck-rules-winner-count">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            6. Number of winners
          </h2>
          <p>
            The top <strong data-testid="cutest-pluck-rules-winner-count-value">
              {display.winnerCount}
            </strong> entries win.
          </p>
        </section>

        {/* 7. Prize — dynamic dollar */}
        <section data-testid="cutest-pluck-rules-prize">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            7. Prize
          </h2>
          <p>
            Each winner receives <strong data-testid="cutest-pluck-rules-prize-value">
              {display.prize}
            </strong> in platform credit, redeemable against a future
            Pluck. No cash value. Credit becomes usable when booking
            opens in the winner's area.
          </p>
        </section>

        {/* 8. Photo use — verbatim consent text */}
        <section data-testid="cutest-pluck-rules-photo-use">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            8. Photo use
          </h2>
          <p>
            When you submit an entry, you check a consent box
            containing this exact statement:
          </p>
          <blockquote
            className="mt-2 border-l-4 border-pp-orange bg-pp-surface2 px-4 py-2 italic"
            data-testid="cutest-pluck-rules-consent-text"
          >
            &ldquo;{CONTEST_CONSENT_TEXT}&rdquo;
          </blockquote>
          <p className="mt-2">
            The consent checkbox appears on the entry form. Your
            entry cannot be submitted without checking it.
          </p>
        </section>

        {/* 9. Voting conduct */}
        <section data-testid="cutest-pluck-rules-voting-conduct">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            9. Voting conduct
          </h2>
          <p>
            <strong>One vote per person per entry across the whole
            contest</strong> — you can move your vote between entries,
            but you can only support one entry at a time.
          </p>
          <p className="mt-2">
            Automated or coordinated voting may result in
            disqualification at PooPluckers' discretion following
            manual review.
          </p>
        </section>

        {/* 10. Winner notification — fork A: BY EMAIL */}
        <section data-testid="cutest-pluck-rules-winner-notification">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            10. Winner notification
          </h2>
          <p>
            <strong>Winners will be notified by email at the address
            on their entry.</strong> Please check your spam folder if
            you don't see the email — automated messages sometimes
            land there.
          </p>
        </section>

        {/* 11. Rejection notification — passive pull */}
        <section data-testid="cutest-pluck-rules-rejection-notification">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            11. If your entry isn't approved
          </h2>
          <p>
            <strong>If your entry is not approved, you'll see the
            status and reason on your entry page.</strong> We do not
            currently send rejection notifications by email.
          </p>
        </section>

        {/* 12. Errors + fraud clause */}
        <section data-testid="cutest-pluck-rules-errors-fraud">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            12. Errors and fraud
          </h2>
          <p>
            PooPluckers may correct errors or void entries obtained
            through fraud or abuse.
          </p>
        </section>

        {/* 13. Social platform disclaimer */}
        <section data-testid="cutest-pluck-rules-platform-disclaimer">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            13. Platform disclaimer
          </h2>
          <p>
            <strong>This promotion is not sponsored, endorsed, or
            administered by, or associated with, Facebook, Instagram,
            or any other platform.</strong>
          </p>
        </section>

        {/* 14. Rules subject to Terms */}
        <section data-testid="cutest-pluck-rules-terms-reference">
          <h2 className="text-lg font-semibold text-pp-text-primary">
            14. Terms of service
          </h2>
          <p>
            These rules are in addition to, and subject to,
            PooPluckers' <Link
              to="/terms"
              className="underline hover:text-pp-orange"
            >Terms of Service</Link>.
          </p>
        </section>

        <footer className="mt-8 border-t border-pp-border pt-4 text-xs text-pp-text-muted">
          <Link
            to="/cutest-pluck"
            className="underline hover:text-pp-orange"
          >
            &larr; Back to the contest
          </Link>
        </footer>
      </article>
    </PublicPageShell>
  );
}
